import { IRetryPolicySettings } from '@/app-modules/server-communications';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { ConfigurationKey } from '@/app-modules/config';
import TombolaBaseComponent from '@/components/base/base.component';
import { User } from '@/app-modules/store/modules/user';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import chatChildComponents from '@/components/chat/_components';
import { RoomUserModel, JackpotPrizeNotificationEventName, ChatMessageModel, ArcadeWinnerNotificationEventName } from '@/components/chat/_domain';
import { IChatServerService, IServerServiceFactory, ServerServiceSubscriptionStore } from '@/app-modules/server-service';
import { ICommandShortcutService, IChatWindowCommandShortcutContext, CommandShortcutOrigin } from '@/components/chat/_domain/modules/command-shortcut-service';
import { __encodeString } from '@/helper/string-extensions/extensions';
import { RoomUserContainer } from '@/components/chat/_domain/modules/room-user-container/room-user-container';
import { UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { IUserChatStatusService } from '@/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service-interface';
import UserListComponent from '@/components/chat/_components/user-list/user-list.component';
import { ISinBanService } from '@/components/chat/_domain/modules/sin-ban-service';
import { OpenContextMenuEventName, ContextMenuConfiguration } from '@/app-modules/tombola-context-menu';
import { IChatPrizeService } from '@/components/chat/_domain/modules/chat-prize-service';
import { ISweetAlertService } from '@/app-modules/sweet-alert-service';
import { ChatFreezeKeyboardShortcutEventHandlerName } from '@/components/chat/_domain/modules/keyboard-shortcut-service/handlers/toggle-chat-freeze';
import { List } from 'lodash';

const DefaultMessageColor = '#ff0000';

@Component({
  name: 'ChatComponent',
  components: chatChildComponents
})
export default class ChatComponent extends TombolaBaseComponent {
  @Prop() public modalGuid: string;
  @Prop() public gameName: string;
  @Prop() public chatRoomName: string;
  @Prop() public chatBackgroundColour: string;
  @Prop() public id: number;
  @Prop() public viewMessageHistory: boolean;
  @Prop() public countMessageHistory: number;
  @Prop() public isStealthOnEnter: boolean;
  @Prop() public isArcade: boolean;
  private userListVisible: boolean = false;
  private isChatFrozen: boolean = false;
  private server: IChatServerService = null;
  private serverSubscriptionStore: ServerServiceSubscriptionStore = null;
  private user: User = this.$tombolaStore.Get(User);
  private isStealth: boolean = false;
  private showEnterAndExitMessages: boolean = true;
  private roomUserContainer: RoomUserContainer = null;
  private chatFreezeKeyboardShortcutEventHandler: (message: string) => void = null;
  private isSe: boolean = this.$tombolaStore.Get(UserPreferences).country.toLowerCase() === 'se';

  private get serverServiceFactory(): IServerServiceFactory {
    return this.$container.get(SERVICE_IDENTIFIERS.IServerServiceFactory) as IServerServiceFactory;
  }

  private get commandShortcutService(): ICommandShortcutService {
    return this.$container.get(SERVICE_IDENTIFIERS.ICommandShortcutService) as ICommandShortcutService;
  }

  private get userChatStatusService(): IUserChatStatusService {
    return this.$container.get(SERVICE_IDENTIFIERS.IUserChatStatusService);
  }

  private get retryPolicySettings(): IRetryPolicySettings {
    return this.server ? this.server.retryPolicySettings : null;
  }

  private get sweetAlertService(): ISweetAlertService {
    return this.$container.get(SERVICE_IDENTIFIERS.ISweetAlertService);
  }

  private get messageBox(): HTMLTextAreaElement {
    return this.$refs.messageBox as HTMLTextAreaElement;
  }

  private get chatSize(): number {
    return this.$tombolaStore.Get(UserPreferences).chatFontSize;
  }

  private get isInternationalCountry(): boolean {
    return this.$tombolaStore.Get(UserPreferences).country.toLowerCase() !== 'uk' ? true : false;
  }

  private async mounted() {
    this.isStealth = this.isStealthOnEnter;
    this.server = await this.serverServiceFactory.createChatService(this.gameName, this.chatRoomName, this.isArcade);
    this.roomUserContainer = new RoomUserContainer(this.configService, this.server, this.gameName, this.chatRoomName);

    Vue.nextTick(() => {
      this.subscribeToServerMessageStreams();
      this.server.connect();

      if (this.isSe){
        this.logInfo('Chat Room Opened', {user: this.user, gameName: this.gameName, chatRoomName: this.chatRoomName, isArcade: this.isArcade });
      }

      if (!this.isCmChat() && !this.isArcade) {
        this.subscribeToBingoJackpotNotificationEvent();
      }

      if (!this.isCmChat() && this.isArcade) {
        this.subscribeToArcadeWinnerNotificationEvent();
      }

      this.subscribeToChatFreezeKeyboardShortcutEvent();
      this.updateTitleBarUserListCount();
    });
  }

  private subscribeToServerMessageStreams() {
    this.serverSubscriptionStore = new ServerServiceSubscriptionStore(
      ...this.roomUserContainer.getUserListStreamSubscriptions(),
      this.roomUserContainer.subscribeUserListChanged(() => this.updateTitleBarUserListCount()),
      this.server.subscribeConnected(this.connectAndAuthToChat),
      this.server.subscribeRetryCircuitBroken(this.handleConnectionRetryCircuitBroken)
    );
  }

  private unsubscribeFromServerMessageStreams() {
    if (this.serverSubscriptionStore) {
      this.serverSubscriptionStore.unsubscribeAll();
    }
  }

  private beforeDestroy() {
    this.unsubscribeFromServerMessageStreams();
    this.unsubscribeToChatFreezeKeyboardShortcutEvent();
    if (!this.isCmChat() && !this.isArcade) {
      this.unsubscribeFromBingoJackpotNotificationEvent();
    }

    if (!this.isCmChat() && this.isArcade) {
      this.unsubscribeFromArcadeWinnerNotificationEvent();
    }

    this.disconnectChat();
  }

  private isCmChat(): boolean {
    return this.gameName === 'cmchat' || this.gameName === 'cschat' || this.gameName === 'CM Chat';
  }

  private connectAndAuthToChat() {
    if (this.server === null) {
      throw new Error('server not initialised');
    }

    const userId: number = this.user.userId;
    const crossCheck: number = this.user.crosscheckId;
    const roomId: number = this.id;
    const clientTypeId: number = this.configService.getConfigurationItem<number>(ConfigurationKey.CLIENT_TYPE_ID);
    const stealthOnEnter: boolean = this.isStealth;
    const authMessage = {
      toPayload() {
        return `<admin command="auth" roomId="${roomId}" id="${userId}" clientTypeId="${clientTypeId}" crossCheck="${crossCheck}" stealth="${stealthOnEnter}"/>`;
      }
    };

    this.server.send(authMessage);
  }

  private handleConnectionRetryCircuitBroken() {
    const err = new Error(`Chat socket connection lost for ${this.gameName}`);
    this.errorService.logError(err, { username: this.user.username, roomId: this.id, crossCheck: this.user.crosscheckId });
  }

  private sendMessageText(message: string, messageColor: string = DefaultMessageColor, logMessage: boolean = false) {
    const cmPrefixPromise = this.userChatStatusService.getCMPrefix();
    cmPrefixPromise.then((cmPrefix) => {
      const username: string = `${cmPrefix}-${this.user.username}`;
      const messageXml = `<FONT COLOR="${messageColor}">${__encodeString(message)}</FONT>`;
      const encodedMessage = __encodeString(messageXml);
      const serverMessage = {
        toPayload() {
          return `<chat command="message" user="${username}" msg="${encodedMessage}"></chat>`;
        }
      };
      if (logMessage){
        this.logInfo(`Sending winner message to server Country: SE`, {messageXml, encodedMessage, message: `<chat command="message" user="${username}" msg="${encodedMessage}"></chat>`});
      }
      this.server.send(serverMessage);
    });
  }

  private sendMessage() {
    const messageBox = this.messageBox;

    if (this.server === null) {
      throw new Error('server not initialised');
    }

    if (messageBox === null) {
      throw new Error('message-box not found');
    }

    const message = messageBox.value;
    if (message) {
      const context = this.getCommandShortcutContext();
      if (!this.commandShortcutService.processCommand(message, context)) {
        this.sendMessageText(message, this.getMessageColor());
      }
    }
    messageBox.value = null;
  }

  private getCommandShortcutContext(): IChatWindowCommandShortcutContext {
    const sinBanService: ISinBanService = this.$container.get(SERVICE_IDENTIFIERS.ISinBanService);
    const chatPrizeService: IChatPrizeService = this.$container.get(SERVICE_IDENTIFIERS.IChatPrizeService);
    const self = this;
    const userList = this.$refs.userList as UserListComponent;
    return {
      sinBinUser: (username: string) => sinBanService.sinBinUser(username, self.server, this.chatRoomName),
      banUser: (username: string) => sinBanService.banUser(username, self.server, this.chatRoomName),
      awardChatPrize: async (usernames: string[]) => {
        if (self.validateUsersAreInRoom(usernames)) {
          await chatPrizeService.awardPlayerChatPrize(usernames, null, self.server);
        }
      },
      toggleUserListSortOrder() {
        self.userListVisible = true;
        userList.toggleSortingOrder();
      },
      commandOrigin: CommandShortcutOrigin.ChatWindow
    };
  }

  private validateUsersAreInRoom(usernames: string[]): boolean {
    if (usernames) {
      const usersNotPresentInRoom = usernames.filter((username) => !this.validateUserIsInRoom(username));
      if (usersNotPresentInRoom && usersNotPresentInRoom.length > 0) {
        this.showValidationErrorMessageForMissingUsers(usersNotPresentInRoom);
        return false;
      }

      return true;
    }
    return false;
  }

  private validateUserIsInRoom(username: string): boolean {
    if (username && this.roomUserContainer) {
      const roomUser = this.roomUserContainer.findRoomUser(username);
      return roomUser !== undefined;
    }
    return false;
  }

  private showValidationErrorMessageForMissingUsers(usersNotPresentInRoom: string[]): void {
    const refocus = true;
    this.sweetAlertService.show({
      title: this.$lang.chat.prize.title,
      html: this.formatValidationErrorMessageForMissingUsers(usersNotPresentInRoom),
      type: 'error',
      showConfirmButton: true
    }, refocus);
  }

  private formatValidationErrorMessageForMissingUsers(usersNotPresentInRoom: string[]): string {
    const errorMessage: string = this.$lang.chat.prize.userValidationError;
    const usernames = usersNotPresentInRoom.join(', ');
    return errorMessage.format({ usernames });
  }

  private getMessageColor(): string {
    if (this.isCmChat()) {
      return this.$tombolaStore.Get(UserPreferences).cmChatColour;
    }
    return this.$tombolaStore.Get(UserPreferences).cmMessageColour;
  }

  public disconnectChat() {
    if (this.server === null) {
      throw new Error('server not initialised');
    }
    this.server.disconnect(false);
  }

  private downloadChatLog() {
    const chatMessageListArray = this.$refs.messageList as any;
    const chatMessages = chatMessageListArray.chatMessageList;
    let chatMessagesAsDownloadFriendlyString: string = '';

    chatMessages.forEach((message: any) => {
      const sanitisedMessageText: string = this.stripHtmlTags(message.messageText);
      const chatMessage: string = message.timestamp + ' ' + message.username + ': ' + sanitisedMessageText + '\r\n';
      chatMessagesAsDownloadFriendlyString = chatMessagesAsDownloadFriendlyString + chatMessage;
    });
    this.createFileDownloadBlob(chatMessagesAsDownloadFriendlyString);
  }

  private createFileDownloadBlob(fileMessageString: string) {
    const date = new Date();
    const today = (date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear()).toString();

    const downloadBlob = new Blob([fileMessageString], { type: 'text/plain' });
    const downloadLink = window.document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(downloadBlob);
    downloadLink.download = this.gameName + ' - ' + this.chatRoomName + ' - Chat Log ' + today + '.txt';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  private stripHtmlTags(message: string): string {
    const regexTagRemoval = /<FONT\b[^>]*>(.*?)<\/FONT>/gmi;
    const match = regexTagRemoval.exec(message);
    return match === null ? message : match[1];
  }

  private toggleUserList() {
    this.userListVisible = !this.userListVisible;
    this.messageBox.focus();
  }

  private toggleChatFreeze() {
    this.isChatFrozen = !this.isChatFrozen;
    this.messageBox.focus();
  }

  private toggleStealthMode() {
    this.isStealth = !this.isStealth;
    this.messageBox.focus();
  }

  private toggleEnterAndExitMessages() {
    this.showEnterAndExitMessages = !this.showEnterAndExitMessages;
    this.messageBox.focus();
  }

  private userListSortOrderChanged() {
    this.messageBox.focus();
  }

  private addUsernameToChatInput(eventArgs: { user: RoomUserModel }) {
    this.messageBox.value += `${eventArgs.user.username} `;
    this.messageBox.focus();
  }

  private subscribeToChatFreezeKeyboardShortcutEvent() {
    this.chatFreezeKeyboardShortcutEventHandler = this.handleChatFreezeKeyboardShortcutEvent.bind(this);
    this.$eventbus.$on(ChatFreezeKeyboardShortcutEventHandlerName, this.chatFreezeKeyboardShortcutEventHandler);
  }

  private unsubscribeToChatFreezeKeyboardShortcutEvent() {
    if (this.chatFreezeKeyboardShortcutEventHandler) {
      this.$eventbus.$off(ChatFreezeKeyboardShortcutEventHandlerName, this.chatFreezeKeyboardShortcutEventHandler);
      this.chatFreezeKeyboardShortcutEventHandler = null;
    }
  }

  private subscribeToBingoJackpotNotificationEvent() {
    this.$eventbus.$on(JackpotPrizeNotificationEventName, this.handleJackpotPrizeNotificationEvent);
  }

  private unsubscribeFromBingoJackpotNotificationEvent() {
    this.$eventbus.$off(JackpotPrizeNotificationEventName, this.handleJackpotPrizeNotificationEvent);
  }

  private subscribeToArcadeWinnerNotificationEvent() {
    if (this.isSe){
      this.logInfo('Subscribed to arcade winner notification event Country: SE', {user: this.user, gameName: this.gameName, chatRoomName: this.chatRoomName, isArcade: this.isArcade });
    }
    this.$eventbus.$on(ArcadeWinnerNotificationEventName, this.handleArcadeWinnerNotificationEvent);
  }

  private unsubscribeFromArcadeWinnerNotificationEvent() {
    if (this.isSe){
      this.logInfo('Unsubscribed to arcade winner notification event Country: SE', {user: this.user, gameName: this.gameName, chatRoomName: this.chatRoomName, isArcade: this.isArcade });
    }
    this.$eventbus.$off(ArcadeWinnerNotificationEventName, this.handleArcadeWinnerNotificationEvent);
  }

  private handleJackpotPrizeNotificationEvent(jackpotMessage: ChatMessageModel) {
    const jackpotAlertPrefix = jackpotMessage.messagePrefix || this.$lang.chat.message.jackpotAlert;
    this.sendMessageText(`${jackpotAlertPrefix} ${jackpotMessage.messageText}`);
  }

  private handleArcadeWinnerNotificationEvent(winnerMessage: ChatMessageModel) {
    if (this.isSe){
      this.logInfo('Handling Arcade Winner Notification', {winnerMessage, chatLang: this.$lang.chat });
    }
    const winnerAlertPrefix = winnerMessage.messagePrefix || this.$lang.chat.message.arcadeWinnerAlert;
    this.sendMessageText(`${winnerAlertPrefix} ${winnerMessage.messageText}`, DefaultMessageColor, this.isSe);
  }

  private handleChatFreezeKeyboardShortcutEvent(modalGuid: string) {
    if (this.modalGuid === modalGuid) {
      this.toggleChatFreeze();
    }
  }

  private launchRoomSwapContextMenu(mouseEvent: MouseEvent) {
    const menuConfig = this.buildRoomSwapMenuConfiguration();
    this.$eventbus.$emit(OpenContextMenuEventName, mouseEvent, menuConfig);
  }

  private buildRoomSwapMenuConfiguration(): ContextMenuConfiguration {
    const modal = this.$modalManager.getModal(this.modalGuid);
    return this.$modalManager.buildRoomSwapMenuConfigurationForModal(modal);
  }

  private updateTitleBarUserListCount(): void {
    const modal = this.$modalManager.getModal(this.modalGuid);
    const userCount = this.roomUserContainer.userListCount();
    modal.modalTitleSuffix = `<span class="badge badge-secondary">${userCount}</span>`;
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/chat.component.ts